import { useParams, Link } from 'react-router-dom';
import { sportsCategories, liveMatches, upcomingMatches, articles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useMemo } from 'react';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = useMemo(() => {
    if (slug === 'promotions') {
      return { slug: 'promotions', name: '优惠活动', icon: 'ri-gift-line', matchCount: 0, description: 'bb体育最新优惠活动与专属福利' };
    }
    if (slug === 'guide') {
      return { slug: 'guide', name: '投注指南', icon: 'ri-book-open-line', matchCount: 0, description: '体育投注技巧与策略指南' };
    }
    return sportsCategories.find((c) => c.slug === slug) || sportsCategories[0];
  }, [slug]);

  const categoryMatches = useMemo(() => {
    return liveMatches.filter((m) => m.category === slug);
  }, [slug]);

  const categoryUpcoming = useMemo(() => {
    return upcomingMatches.filter((m) => m.category === slug);
  }, [slug]);

  const categoryArticles = useMemo(() => {
    return articles.filter((a) => a.category === slug);
  }, [slug]);

  const categoryName = currentCategory?.name || '赛事';

  return (
    <div className="pt-16">
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: `${categoryName}赛事` },
        ]}
      />

      <section className="w-full px-4 md:px-6 py-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <i className={`${currentCategory.icon} text-lg text-primary-500`}></i>
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">
                {categoryName}赛事
              </h1>
              <p className="text-xs text-foreground-500">{currentCategory.description}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
            {sportsCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-xs rounded-full whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <i className={cat.icon}></i>
                {cat.name}
              </Link>
            ))}
            <Link
              to="/category/promotions"
              className={`px-4 py-2 text-xs rounded-full whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                slug === 'promotions'
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
              }`}
            >
              <i className="ri-gift-line"></i>
              优惠活动
            </Link>
          </div>

          {categoryMatches.length > 0 && (
            <div className="mb-8">
              <h2 className="text-base font-heading font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-live-line text-primary-500"></i>
                正在直播
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryMatches.map((match) => (
                  <div
                    key={match.id}
                    className="bg-background-100 rounded-lg p-4 md:p-5 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-foreground-500">{match.league}</span>
                      <span className="flex items-center gap-1 text-xs text-primary-500 font-medium">
                        <span className="w-1.5 h-1.5 bg-primary-500 rounded-full animate-pulse"></span>
                        直播中
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex flex-col items-center gap-2 flex-1">
                        <div className="w-11 h-11 rounded-full bg-background-200 overflow-hidden flex items-center justify-center">
                          <img src={match.homeLogo} alt={match.homeTeam} className="w-full h-full object-cover" />
                        </div>
                        <span className="text-xs text-foreground-700 text-center whitespace-nowrap">{match.homeTeam}</span>
                      </div>
                      <div className="flex flex-col items-center px-3">
                        <span className="text-xl font-heading font-bold text-foreground-900 whitespace-nowrap">{match.score}</span>
                        <span className="text-xs text-primary-500">{match.minute}</span>
                      </div>
                      <div className="flex flex-col items-center gap-2 flex-1">
                        <div className="w-11 h-11 rounded-full bg-background-200 overflow-hidden flex items-center justify-center">
                          <img src={match.awayLogo} alt={match.awayTeam} className="w-full h-full object-cover" />
                        </div>
                        <span className="text-xs text-foreground-700 text-center whitespace-nowrap">{match.awayTeam}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="flex-1 py-1.5 text-xs bg-background-200 hover:bg-primary-100 rounded transition-colors whitespace-nowrap text-center">
                        <span className="block text-foreground-400">主胜</span>
                        <span className="block font-semibold text-foreground-800">{match.odds.home}</span>
                      </button>
                      {match.odds.draw !== undefined && (
                        <button className="flex-1 py-1.5 text-xs bg-background-200 hover:bg-primary-100 rounded transition-colors whitespace-nowrap text-center">
                          <span className="block text-foreground-400">平</span>
                          <span className="block font-semibold text-foreground-800">{match.odds.draw}</span>
                        </button>
                      )}
                      <button className="flex-1 py-1.5 text-xs bg-background-200 hover:bg-primary-100 rounded transition-colors whitespace-nowrap text-center">
                        <span className="block text-foreground-400">客胜</span>
                        <span className="block font-semibold text-foreground-800">{match.odds.away}</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {categoryUpcoming.length > 0 && (
            <div className="mb-8">
              <h2 className="text-base font-heading font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-calendar-todo-line text-accent-500"></i>
                即将开赛
              </h2>
              <div className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                <div className="divide-y divide-background-200/70">
                  {categoryUpcoming.map((match) => (
                    <div key={match.id} className="flex items-center justify-between px-4 py-3 hover:bg-background-200/40 transition-colors">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{match.homeTeam}</span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">VS</span>
                        <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{match.awayTeam}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-foreground-500 whitespace-nowrap hidden sm:block">{match.time}</span>
                        <div className="flex items-center gap-1">
                          <button className="px-3 py-1 text-xs bg-background-200 hover:bg-primary-100 rounded transition-colors whitespace-nowrap">
                            <span className="font-semibold text-foreground-700">{match.odds.home}</span>
                          </button>
                          {match.odds.draw !== undefined && (
                            <button className="px-3 py-1 text-xs bg-background-200 hover:bg-primary-100 rounded transition-colors whitespace-nowrap">
                              <span className="font-semibold text-foreground-700">{match.odds.draw}</span>
                            </button>
                          )}
                          <button className="px-3 py-1 text-xs bg-background-200 hover:bg-primary-100 rounded transition-colors whitespace-nowrap">
                            <span className="font-semibold text-foreground-700">{match.odds.away}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {categoryArticles.length > 0 && (
            <div>
              <h2 className="text-base font-heading font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-article-line text-primary-500"></i>
                {categoryName}资讯
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all group"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        title={`${article.title} - bb体育`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-medium text-foreground-800 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {categoryMatches.length === 0 && categoryUpcoming.length === 0 && categoryArticles.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">暂无{categoryName}相关赛事，敬请期待</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}